import { createFileRoute, notFound } from "@tanstack/react-router";
import { getPost, getTranslation } from "@/lib/blog";
import { BlogPostLayout } from "@/components/BlogPostLayout";

export const Route = createFileRoute("/blog/$slug")({
  loader: ({ params }) => {
    const post = getPost(params.slug, "en");
    if (!post) throw notFound();
    const esPost = getTranslation(post.translationKey, "es");
    return { post, esPost };
  },
  head: ({ loaderData }) => {
    const post = loaderData?.post;
    const esPost = loaderData?.esPost;
    const canonicalUrl = `https://adkavi.ai/blog/${post?.slug ?? ""}`;
    const esUrl = esPost ? `https://adkavi.ai/es/blog/${esPost.slug}` : undefined;
    return {
      meta: [
        { title: post ? `${post.title} — AdKavi` : "Blog — AdKavi" },
        { name: "description", content: post?.description ?? "" },
        { property: "og:title", content: post?.title ?? "" },
        { property: "og:description", content: post?.description ?? "" },
        { property: "og:type", content: "article" },
        { property: "og:locale", content: "en_US" },
        { property: "og:url", content: canonicalUrl },
        ...(post?.ogImage ? [{ property: "og:image", content: post.ogImage }] : []),
        ...(post?.ogImage ? [{ name: "twitter:image", content: post.ogImage }] : []),
        { name: "twitter:card", content: "summary_large_image" },
        { name: "twitter:title", content: post?.title ?? "" },
        { name: "twitter:description", content: post?.description ?? "" },
      ],
      links: [
        { rel: "canonical", href: canonicalUrl },
        { rel: "alternate", hreflang: "en", href: canonicalUrl },
        ...(esUrl
          ? [
              { rel: "alternate", hreflang: "es", href: esUrl },
              { rel: "alternate", hreflang: "x-default", href: canonicalUrl },
            ]
          : [{ rel: "alternate", hreflang: "x-default", href: canonicalUrl }]),
      ],
      scripts: post
        ? [
            {
              type: "application/ld+json",
              children: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Article",
                headline: post.title,
                description: post.description,
                datePublished: post.date,
                inLanguage: "en-US",
                url: canonicalUrl,
                author: { "@type": "Organization", name: "AdKavi", url: "https://adkavi.ai" },
                publisher: {
                  "@type": "Organization",
                  name: "AdKavi",
                  url: "https://adkavi.ai",
                  logo: {
                    "@type": "ImageObject",
                    url: "https://adkavi.ai/favicon.ico",
                  },
                },
                ...(post.ogImage ? { image: post.ogImage } : {}),
              }),
            },
          ]
        : [],
    };
  },
  component: EnBlogPost,
});

function EnBlogPost() {
  const { post, esPost } = Route.useLoaderData();
  return <BlogPostLayout post={post} alternatePost={esPost} lang="en" />;
}
